/**
 * Ingreso a la consola de la plataforma (SuperAdmin), con el mismo marco que el ingreso
 * de las cuentas: regla del espectro, logo, y el panel índigo de Finance360.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, AlertTriangle } from 'lucide-react';
import useSuperadminAuthStore from '../store/authStore';
import MarcoAcceso from '../../components/MarcoAcceso';

// Lo que hay adentro de la consola, con los mismos nombres del menú
const TARJETA_PLATAFORMA = (
  <div className="flujo" aria-hidden="true">
    <div className="flujo-cab">
      <span className="tipo">360</span>
      <span>Consola de la plataforma</span>
      <span className="chip ok"><i />En vivo</span>
    </div>
    <ol>
      <li className="hecho"><b>Cuentas</b><span>Empresas, usuarios, planes y sesión de soporte</span></li>
      <li className="hecho"><b>Rechazos de Hacienda</b><span>Cada rechazo con su motivo y seguimiento</span></li>
      <li className="hecho"><b>Respaldos</b><span>Por cuenta y por empresa, en Azure</span></li>
      <li className="hecho"><b>Alertas por correo</b><span>Rechazos, certificados y respaldos fallidos</span></li>
    </ol>
    <div className="modulos">
      {['Resumen', 'Auditoría', 'Apariencia de las cuentas'].map(m => <span key={m}>{m}</span>)}
    </div>
  </div>
);

export default function Login() {
  const [email, setEmail] = useState('');
  // Aviso dejado por el interceptor cuando el servidor rechazó un token vencido.
  const [aviso] = useState(() => { const v = sessionStorage.getItem('superadmin_aviso'); sessionStorage.removeItem('superadmin_aviso'); return v; });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { login, isLoading, error, clearError, isAuthenticated, verifyToken } = useSuperadminAuthStore();

  // Si hay sesión guardada, se confirma con el servidor ANTES de redirigir: un
  // token vencido o revocado no puede "entrar" al panel.
  useEffect(() => {
    if (!isAuthenticated) return;
    let vivo = true;
    verifyToken().then((ok) => { if (vivo && ok) navigate('/superadmin'); });
    return () => { vivo = false; };
  }, [isAuthenticated, navigate, verifyToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    const result = await login(email, password);
    if (result.success) navigate('/superadmin');
  };

  return (
    <MarcoAcceso
      rotulo="Consola de la plataforma"
      titular="Todas las cuentas de Finance360, desde un solo lugar."
      texto="Salud de cada cuenta, lo que Hacienda rechaza, los respaldos y las alertas, en vivo."
      tarjeta={TARJETA_PLATAFORMA}
      pie="Acceso restringido a la administración de la plataforma"
    >
      <h2>Consola de la plataforma</h2>
      <p className="bajada">Solo para quienes administran Finance360.</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {aviso && !error && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">{aviso}</div>
        )}
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />{error}
          </div>
        )}

        <div>
          <label htmlFor="sa-correo">Correo electrónico</label>
          <input id="sa-correo" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="campo" placeholder="usted@kuantabridge.com" autoComplete="email" required autoFocus />
        </div>

        <div>
          <label htmlFor="sa-clave">Contraseña</label>
          <div className="relative">
            <input id="sa-clave" type={showPassword ? 'text' : 'password'} value={password}
              onChange={(e) => setPassword(e.target.value)} className="campo" style={{ paddingRight: 44 }}
              placeholder="••••••••••••" autoComplete="current-password" required />
            <button type="button" className="ver" onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button type="submit" disabled={isLoading} className="boton">
          {isLoading ? (
            <>
              <span className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Ingresando...
            </>
          ) : (
            <>Entrar<ArrowRight className="h-5 w-5" /></>
          )}
        </button>
      </form>
    </MarcoAcceso>
  );
}
